import React, {useCallback, useContext, useMemo, useState} from 'react';
import ls from './FormEditor.less';
import { CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons';
import { CheckCircleTwoTone, TagTwoTone, WarningTwoTone } from '@ant-design/icons';
import {BindContext, FormContext} from '../components/metadata/MDForm';
import {ConfigContext, DynamicComponentsContext, PortalContext} from '../components/PortalApp';
import {DeclaredComponent, DeclaredComponentWrapper} from '../components/DynamicComponent';
import {Field} from '../components/metadata/Field';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';
import * as ReactDOM from 'react-dom';
import cx from 'classnames';
import Droppable from './Droppable';
import {PropertiesEditor} from './PropertiesEditor';
import {Group} from '../components/layout/Group';
import {propsToCSS} from '../components/css';
import {Expression} from '../components/metadata/Expression';
import {DesignErrorScope} from './DesignErrorScope';
import {Data} from '../components/metadata/Data';
import {fromJS, isImmutable, isIndexed, isKeyed} from 'immutable';


export function FormEditor({presentationMetadata, rcForm}) {

  const {editMode} = useContext(PortalContext);
  const {refreshConfig} = useContext(PortalContext);
  const [editableForm, setEditableForm] = useState(false);


  return <React.Fragment>
    {editMode && <div className={ls.formMarker} onClick={() => setEditableForm({})}/>}
    {editableForm && <div className={ls.overlay}/>}
    {editableForm && <Editor presentationMetadata={presentationMetadata} rcForm={rcForm} close={
      (newForm) => {
        setEditableForm(null);
        refreshConfig(presentationMetadata, newForm.toJS());
      }
    }/>}
  </React.Fragment>

}

const m = new Map();
let c = 0;
function getId(o) {

  let id = m.get(o);
  if (!id) {
    id = ++c;
    m.set(o, id)
  }

  return id;
}


function Editor({presentationMetadata: initialPresentationMetadata, rcForm, close}) {

  const {metadata, prefix, getValue} = useContext(FormContext);
  const {config} = useContext(ConfigContext);

  const [presentationMetadata, setPresentationMetadata] = useState(() => fromJS(setInstanceIds(initialPresentationMetadata)));

  const updateIn = (path, updater) => setPresentationMetadata(presentationMetadata => presentationMetadata.updateIn(path, updater));
  const setIn = (path, value) => setPresentationMetadata(presentationMetadata => presentationMetadata.setIn(path, value));

  const pathMap = new Map();
  mapObjectPaths(presentationMetadata, pathMap);

  const consumedAttributes = new Set();
  traverseComponentTree(presentationMetadata, (compName, props) => {
    const attrId = props.get('attr');
    if (compName === 'Field' && attrId) {
      consumedAttributes.add(attrId);
    }
  });

  const componentInstances = new Map();
  traverseComponentTree(presentationMetadata, (compName, props, node) => {
    const instanceId = props.get('__instance_id');
    componentInstances.set(instanceId, node);
  });

  const dynamicComponents = replaceToDesignTimeComponents(useContext(DynamicComponentsContext));

  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [highlightedInstanceId, setHighlightedInstanceId] = useState(null);
  const [hint, setHint] = useState(null);

  const setHighlightedElement = (declaration) => {
    highlightByInstanceId(declaration && declaration.valueSeq().get(0).get('__instance_id'));
  };

  const setSelectedElement = (declaration) => {
    selectByInstanceId(declaration && declaration.valueSeq().get(0).get('__instance_id'));
  };

  const highlightByInstanceId = id => {
    highlightDomElement(id);
    setHighlightedInstanceId(id)
  };

  const selectByInstanceId = id => {
    const instance = componentInstances.get(id);
    selectDomElement(id);
    setSelectedInstanceId(id);
  };

  function closeAndCleanUp() {
    highlightDomElement(null);
    selectDomElement(null);
    close(presentationMetadata);
  }

  const highlightedElement = componentInstances.get(highlightedInstanceId);
  const selectedElement = componentInstances.get(selectedInstanceId);
  const selectedProps = selectedElement && selectedElement.valueSeq().get(0);
  const selectedComponent = selectedElement && dynamicComponents[selectedElement.keySeq().get(0)];
  const selectedSchema = selectedComponent && selectedComponent.schema;
  const selectedPath = pathMap.get(selectedProps);

  const onDropOntoNode = (node, data, type, before, direct) => {
    let toAdd = {};
    if (type === 'component') {
      toAdd = {
        [data]: {}
      };
      if (toAdd.Group) {
        toAdd.Group.content = [];
      }
    } else if (type === 'attribute') {
      toAdd = {
        Field: {
          attr: data
        }
      }
    } else {
      return;
    }
    const nodePath = pathMap.get(node);
    if (!nodePath) {
      return;
    }
    Object.values(toAdd)[0].__instance_id = genId();
    toAdd = fromJS(toAdd);
    if (direct) {
      updateIn(nodePath, node => node.push(toAdd));
    } else {
      const parentPath = [...nodePath];
      const pos = parentPath.pop();

      updateIn(parentPath, parent => parent.insert(before ? pos : pos + 1, toAdd));
    }
  };

  return <BindContext.Provider value={{bind: () => {}}}>
    <DynamicComponentsContext.Provider value={dynamicComponents} >
       <div className={ls.mainContainer}>
        <h3>Form Editor: {prefix}` <button onClick={closeAndCleanUp}>close</button></h3>
        <div className={ls.editorContent}>


          <SplitterLayout primaryIndex={1} secondaryInitialSize={250} >
            <div>
              <FunctionalArea title='Attributes'>
                <AttributeList attributes={metadata} consumedAttributes={consumedAttributes}/>
              </FunctionalArea>

            </div>
            <SplitterLayout primaryIndex={1} secondaryInitialSize={200}>
              <FunctionalArea title='Components'>
                <ComponentList />
              </FunctionalArea>
              <SplitterLayout primaryIndex={1} secondaryInitialSize={300}>
                <FunctionalArea title='Component Tree'>
                  <PresentationMDTree presentationMetadata={presentationMetadata} highlighted={highlightedElement} selected={selectedElement} highlight={setHighlightedElement} select={setSelectedElement} onDrop={onDropOntoNode}/>
                </FunctionalArea>
                <SplitterLayout primaryIndex={0} vertical={true} secondaryInitialSize={200}>
                  <div className={ls.mockUp}>
                    <FunctionalArea title='Design Mockup'>
                        <MockUp presentationMetadata={presentationMetadata} setHint={setHint} select={selectByInstanceId} highlight={highlightByInstanceId}/>
                    </FunctionalArea>
                    {hint && <div className={ls.mockUpHint} >{hint}</div>}
                  </div>

                  <FunctionalArea title='Properties'>
                    <PropertiesEditor props={selectedProps} schema={selectedSchema} onChange={(path, value) => setIn([...selectedPath, ...path], value)} />
                  </FunctionalArea>
                </SplitterLayout>
              </SplitterLayout>


            </SplitterLayout>

          </SplitterLayout>


        </div>
      </div>
    </DynamicComponentsContext.Provider>
  </BindContext.Provider>

}

function FunctionalArea({title, children}) {
  return <div className={ls.fullHeight}>
    <h4 className={ls.title}>{title}</h4>
    <div className={ls.fill}>{children}</div>
  </div>;
}

function replaceToDesignTimeComponents(components) {

  components = {

    ...components,

    // Field: ({attr}) => <div>{attr.label}: <Inline>#{attr}</Inline></div>,

    Expression: DesignTimeExpression,

    Group: DesignTimeGroup,

    Data: DesignTimeData,

  };

  // Object.keys(key => components[key] = remap(components[key]));

  return components;
}


// const designTimeComponents = new Map();
// function remap(Component) {
//   //it's very important to keep components functions the same. Otherwise react will constantly unmount the tree
//   let designTimeComponent = designTimeComponents.get(Component);
//   if (!designTimeComponent) {
//     designTimeComponent = props => <DesignErrorScope><Component {...props} /></DesignErrorScope>;
//     designTimeComponents.set(Component, designTimeComponent);
//   }
//   return designTimeComponent;
// }

function DesignTimeData({path, attr, ...props}) {
  return <span {...propsToCSS(props)}>{path ? '${' + path + '}' : attr ?  '#{' + attr + '}' : '<?data ref?>' }</span>;
}

DesignTimeData.schema = Data.schema;


function DesignTimeExpression({value, ...props}) {
  return <span {...propsToCSS(props)}>{value}</span>;
}

DesignTimeExpression.schema = Expression.schema;


function DesignTimeGroup({class: classes, ...props}) {
  return <Group class={cx(ls.designTimeGroup, classes)} {...props} />;
}

DesignTimeGroup.schema = Group.schema;


function toComponentAndProps(declaration) {
  const entrySeq = declaration.entrySeq();
  return [...entrySeq.get(0)];
}

function PresentationMDTree({presentationMetadata, highlighted, selected, highlight, select, onDrop}) {

  const [compType, props] = toComponentAndProps(presentationMetadata);

  const [expanded, setExpanded] = useState(true);

  const content = props.get('content');

  const hasChildren = !(!content || !isIndexed(content) || content.size === 0);

  const showReceiver = compType === 'Group' && !hasChildren;

  const expandIconClickAction = e => {
    e.stopPropagation();
    setExpanded(!expanded)
  };

  return (
    <div className={ls.treeNode}>
      <Droppable onDrop={(a,b,c) => onDrop(presentationMetadata, a,b,c)} supportedTypes={['component', 'attribute']}
        className={cx(ls.treeNodeLabel, presentationMetadata === selected && ls.selectedTreeNode, presentationMetadata === highlighted && ls.highlightedTreeNode)}
        onMouseEnter={() => highlight(presentationMetadata)} onMouseLeave={() => highlight(null)} onClick={()=>select(presentationMetadata)}>
        {
          hasChildren && (expanded
            ? <CaretDownOutlined onClick={expandIconClickAction} />
            : <CaretRightOutlined onClick={expandIconClickAction} />)
        }
        <ComponentLabel compType={compType} compProps={props} />
      </Droppable>
      {expanded && content &&
      (isIndexed(content) ?
        content.map((node, i) => <PresentationMDTree highlighted={highlighted} selected={selected} highlight={highlight} select={select} presentationMetadata={node} onDrop={onDrop} key={i}/> ) :
                                 <PresentationMDTree highlighted={highlighted} selected={selected} highlight={highlight} select={select} presentationMetadata={content} onDrop={onDrop}/>)}

      {showReceiver && <Droppable onDrop={(a,b,c) => onDrop(content, a,b,c, true)} supportedTypes={['component', 'attribute']} className='receiver'>[empty: drop to add]</Droppable>}
    </div>
  );

}

function ComponentLabel({compType, compProps, ...props}) {

  let label;
  if (compType === 'Field') {
    label = `Field ${compProps.get('attr')}`
  } else {
    label = compType;
  }

  return <span {...props}> {label} </span>

}

function ComponentList({}) {
  const components = useContext(DynamicComponentsContext);



  return <div className={ls.listView}>

    {Object.keys(components).sort().map(compType => <div draggable={true} onDragStart={e => e.dataTransfer.setData('component', compType)} >
      {compType}
    </div>) }

  </div>;

}

function AttributeList({attributes, consumedAttributes}) {
  return <div className={ls.listView}>

    {Object.keys(attributes).sort().map(attrId => {
      const attr = attributes[attrId];
      return <div key={attrId} draggable={true} onDragStart={e => e.dataTransfer.setData('attribute', attrId)}>
        {getIconForAttribute(attrId, attr, consumedAttributes)} <span className={cx(attr.requiredInd && 'bold')}> {attrId}</span>
      </div>
    }) }

  </div>;

}

function getIconForAttribute(attrId, attr, consumedAttributes) {

  if (attr.requiredInd) {
    if (consumedAttributes.has(attrId)) {
      return <CheckCircleTwoTone twoToneColor="#52c41a" />;
    } else {
      return <WarningTwoTone twoToneColor="#EBAF4C" />;
    }
  } else {
    return <TagTwoTone twoToneColor="#63b9ef" />;
  }
}

const instanceToDom = new Map();
let highlighted, selected;

function highlightDomElement(instanceId) {
  if (highlighted) {
    highlighted.classList.remove('editor-highlighted');
    highlighted = null;
  }

  if (instanceId === null) {
    return;
  }
  const dom = instanceToDom.get(instanceId);

  if (dom) {
    highlighted = dom;
    highlighted.classList.add('editor-highlighted');
  }
}

function selectDomElement(instanceId) {
  if (selected) {
    selected.classList.remove('editor-selected');
    selected = null;
  }

  if (instanceId === null) {
    return;
  }
  const dom = instanceToDom.get(instanceId);

  if (dom) {
    selected = dom;
    selected.classList.add('editor-selected');
  }
}

class EditWrapper extends React.Component {

  componentDidMount() {
    const dom = ReactDOM.findDOMNode(this);
    if (dom) {
      const instanceId = this.props.declaredProps.__instance_id;
      instanceToDom.set(instanceId, dom);
      dom.dataset.componentInstanceId = instanceId;
      dom.dataset.componentId = this.props.componentId;
    }
  }

  componentDidUpdate() {
    this.componentDidMount();
  }

  componentWillUnmount() {
    instanceToDom.delete(this.props.declaredProps.__instance_id);
  }

  render() {
    return <DesignErrorScope>{this.props.children}</DesignErrorScope>;
  }

}

function getHoveredCompNode(target) {
  if (!target || !target.dataset) {
    return null;
  }
  if (target.dataset.componentInstanceId) {
    return target
  }
  return getHoveredCompNode(target.parentNode);
}

function MockUp({presentationMetadata, highlight, select, setHint}) {

  const wrapper = (comp, declaration, componentId, declaredProps) => {
    return <EditWrapper componentId={componentId} declaration={declaration} declaredProps={declaredProps}>
      {comp}
    </EditWrapper>
  };

  return <div

    style={{
      width: '100%',
      height: '100%',
      overflow: 'scroll'
    }}

    onMouseOver={e => {
      const node = getHoveredCompNode(e.target);
      if (node) {
        e.stopPropagation();
        highlight(node.dataset.componentInstanceId);
        setHint(node.dataset.componentId);
      } else {
        highlight(null);
        setHint(null);
      }
    }}
    onMouseLeave={e => {
      e.stopPropagation();
      highlight(null);
      setHint(null);
    }}
    onClick={e => {
      e.stopPropagation();
      const node = getHoveredCompNode(e.target);
      if (node) {
        e.stopPropagation();
        select(node.dataset.componentInstanceId);
      }
    }}
  >
    <DeclaredComponentWrapper.Provider value={wrapper}>
      <DeclaredComponent declaration={presentationMetadata.toJS()} />
    </DeclaredComponentWrapper.Provider>
  </div>;

}

function mapObjectPaths(obj, map, path = [], visited = new Set()) {

  if (visited.has(obj)) {
    return;
  }
  visited.add(obj);

  if (isIndexed(obj)) {
    obj.forEach((c, i) => {
      if (isImmutable(c)) {
        const childPath = [...path, i];
        map.set(c, childPath);
        mapObjectPaths(c, map, childPath, visited);
      }
    })
  } else if (isKeyed(obj)) {
    obj.entrySeq().forEach(([key, c]) => {
      if (isImmutable(c)) {
        const childPath = [...path, key];
        map.set(c, childPath);
        mapObjectPaths(c, map, childPath, visited);
      }
    });
  }

}

function traverseComponentTree(root, callback) {
  const [compType, props] = toComponentAndProps(root);
  callback(compType, props, root);
  const content = props.get('content');
  if (content) {
    if (isIndexed(content)) {
      content.forEach(child => traverseComponentTree(child, callback));
    } else {
      traverseComponentTree(content, callback);
    }
  }
}

let ID_COUNTER = 0;

function genId() {
  return (++ID_COUNTER) + '';
}

function setInstanceIds(declaration) {

  // declaration = JSON.parse(JSON.stringify(declaration));

  setInstanceIdsImpl(declaration);

  return declaration;

}

function setInstanceIdsImpl(declaration) {

  const componentId = Object.keys(declaration)[0];
  const props = declaration[componentId];
  props.__instance_id = genId();

  if (props.content) {
    if (Array.isArray(props.content)) {
      props.content.forEach(child => setInstanceIds(child));
    } else {
      setInstanceIdsImpl(props.content);
    }
  }
}
